import { useEffect, useMemo, useRef, useState } from 'react';
import { CncEquipment } from '../../../shared/types/mtlinkiTypes.ts';
import { dateWithoutTime } from '../../../shared/utils/dateUtils.ts';
import { OeeRow } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { groupPerEquipment, LiveDataPerEquipment } from './groupPerEquipment.ts';


interface Data {
    dataAll: DataAll | undefined;
    dataPerEquipment: Map<string, LiveDataPerEquipment> | undefined;
}

interface DataAll {
    equipments: CncEquipment[];
    shiftTable: OeeRow[];
    hourTable: OeeRow[];
    liveTable: OeeRow[];
}


export function useFetchOees(): Data {

    const intervalRef = useRef<number | null>(null);
    const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(getDateRange());


    const { data: equipments } = trpc.equipments.getAllCnc.useQuery(
        undefined,
        {
            refetchInterval: 2 * 60_000,
            placeholderData: (prev) => prev,
        }
    );

    const { data: shiftTable } = trpc.graphsData.getOee.useQuery({
        windowType: 'shift',
        start: dateRange.start,
        end: dateRange.end,
    }, {
        placeholderData: (prev) => prev,
    });

    const { data: hourTable } = trpc.graphsData.getOee.useQuery({
        windowType: 'hour',
        start: dateRange.start,
        end: dateRange.end,
    }, {
        placeholderData: (prev) => prev,
    });

    const { data: liveTable } = trpc.graphsData.getOee.useQuery({
        windowType: 'live',
        start: dateRange.start,
        end: dateRange.end,
    }, {
        placeholderData: (prev) => prev,
    });


    // Refetch graphs data
    useEffect(() => {
        intervalRef.current = window.setInterval(() => {
            setDateRange(getDateRange());
        }, 1 * 60 * 1000);

        return () => {
            if (intervalRef.current !== null) {
                window.clearInterval(intervalRef.current);
            }
        };
    }, []);

    function getDateRange(): { start: Date; end: Date } {
        const start = dateWithoutTime(new Date());
        start.setDate(start.getDate() - 7);
        const end = new Date();
        return { start, end };
    }

    // Compile return data

    const dataAll = useMemo(() => {
        if (!equipments || !shiftTable || !hourTable || !liveTable) return undefined;
        return {
            equipments,
            shiftTable,
            hourTable,
            liveTable,
        };
    }, [equipments, shiftTable, hourTable, liveTable]);

    const dataPerEquipment = useMemo(() => {
        if (!dataAll) return undefined;
        const perEquipment = groupPerEquipment(dataAll);
        return perEquipment;
    }, [dataAll]);

    return {
        dataAll,
        dataPerEquipment,
    }
}